import { IssueTypeIcon, PriorityIcon } from '@/tracker/entities/issue';
import { StatusBadge } from '@/tracker/entities/status';
import { UserChip } from '@/tracker/entities/user';
import { formatEstimation } from '@/tracker/entities/worklog';
import { TaskBasic, TaskSearchRequest, TaskStatusType } from '@/tracker/types';
import { formatDate } from '@/tracker/utils/formatDate';

export type IssueSort = NonNullable<TaskSearchRequest['orderBy']>[number];
export type IssueSortField = IssueSort['field'];

export const DEFAULT_SORT: IssueSort = { field: 'updateTime', direction: 'desc' };

export type ColumnId =
  | 'priority'
  | 'type'
  | 'key'
  | 'summary'
  | 'status'
  | 'assignee'
  | 'queue'
  | 'createdBy'
  | 'originalEstimation'
  | 'estimation'
  | 'spent'
  | 'deadline'
  | 'createTime'
  | 'updateTime';

export type IssueColumn = {
  id: ColumnId;
  label: string;
  /** Иконочная колонка: заголовок без текста, узкая */
  isIcon?: boolean;
  /** Ключ и задачу не скрыть — без них список не прочитать */
  isRequired?: boolean;
  /** Поле сортировки сервера; без него колонка не сортируется */
  sortField?: IssueSortField;
  /** Без render ячейку рисует сам список: там ссылки и родитель */
  render?: (issue: TaskBasic) => React.ReactNode;
};

/** Порядок колонок в таблице — как здесь */
export const COLUMNS: IssueColumn[] = [
  {
    id: 'priority',
    label: 'Приоритет',
    isIcon: true,
    sortField: 'priority',
    render: (issue) => <PriorityIcon priority={issue.priority} />,
  },
  {
    id: 'type',
    label: 'Тип',
    isIcon: true,
    render: (issue) => <IssueTypeIcon type={issue.type} />,
  },
  { id: 'key', label: 'Ключ', isRequired: true, sortField: 'key' },
  { id: 'summary', label: 'Задача', isRequired: true },
  {
    id: 'status',
    label: 'Статус',
    sortField: 'status',
    render: (issue) => <StatusBadge status={issue.status} />,
  },
  {
    id: 'assignee',
    label: 'Исполнитель',
    render: (issue) => (issue.assignee ? <UserChip name={issue.assignee.displayName} /> : '—'),
  },
  { id: 'queue', label: 'Очередь' },
  {
    id: 'createdBy',
    label: 'Автор',
    render: (issue) => (issue.createdBy ? <UserChip name={issue.createdBy.displayName} /> : '—'),
  },
  {
    id: 'originalEstimation',
    label: 'Оценка',
    render: (issue) => formatEstimation(issue.originalEstimation),
  },
  { id: 'estimation', label: 'Осталось', render: (issue) => formatEstimation(issue.estimation) },
  { id: 'spent', label: 'Затрачено', render: (issue) => formatEstimation(issue.spent) },
  {
    id: 'deadline',
    label: 'Дедлайн',
    sortField: 'deadline',
    render: (issue) => formatDate(issue.deadline),
  },
  {
    id: 'createTime',
    label: 'Создана',
    sortField: 'createTime',
    render: (issue) => formatDate(issue.createTime),
  },
  {
    id: 'updateTime',
    label: 'Обновлена',
    sortField: 'updateTime',
    render: (issue) => formatDate(issue.updateTime),
  },
];

export type GroupBy = 'none' | 'status' | 'assignee' | 'type' | 'priority' | 'queue';

export type IssueGroup = { id: string; title: string; issues: TaskBasic[] };

const STATUS_TYPE_ORDER: TaskStatusType[] = ['new', 'in_progress', 'paused', 'done', 'cancelled'];

type GroupConfig = {
  label: string;
  /** id и заголовок группы задачи */
  groupOf: (issue: TaskBasic) => { id: string; title: string };
  /** Порядок групп; без него — как встретились в списке */
  compare?: (a: TaskBasic, b: TaskBasic) => number;
};

export const GROUPS: Record<Exclude<GroupBy, 'none'>, GroupConfig> = {
  status: {
    label: 'Статус',
    groupOf: (issue) => ({ id: issue.status.key, title: issue.status.name }),
    // Как в Яндексе: сначала новые, потом в работе, закрытые в конце
    compare: (a, b) =>
      STATUS_TYPE_ORDER.indexOf(a.status.type) - STATUS_TYPE_ORDER.indexOf(b.status.type),
  },
  assignee: {
    label: 'Исполнитель',
    groupOf: (issue) => ({
      id: issue.assignee?.id ?? '',
      title: issue.assignee?.displayName ?? 'Не назначен',
    }),
  },
  type: {
    label: 'Тип',
    groupOf: (issue) => ({ id: issue.type.key, title: issue.type.name }),
  },
  priority: {
    label: 'Приоритет',
    groupOf: (issue) => ({ id: issue.priority.key, title: issue.priority.name }),
  },
  queue: {
    label: 'Очередь',
    groupOf: (issue) => ({ id: issue.queue.key, title: issue.queue.name }),
  },
};
